import { useParams } from "react-router-dom";
import { LocalizedLink } from "@/i18n/language";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import { tours } from "./data";
import NotFound from "@/pages/NotFound";
import { usePriceMap } from "@/hooks/usePrices";

const RAFTING_PRICE_INCLUDES = [
  "rafting_incl_lunch",
  "rafting_incl_locker",
  "rafting_incl_shower",
  "rafting_incl_towel",
  "rafting_incl_transfer",
  "rafting_incl_insurance",
  "rafting_incl_rafting",
  "rafting_incl_lifejacket",
  "rafting_incl_buffet",
  "rafting_incl_drybag",
];

const RAFTING_PACKAGE = [
  "rafting_pkg_transfer",
  "rafting_pkg_locker",
  "rafting_pkg_towel",
  "rafting_pkg_lunch",
  "rafting_pkg_insurance",
];

const RAFTING_PICKUP_MORNING = [
  "rafting_pickup_morning_1",
  "rafting_pickup_morning_2",
  "rafting_pickup_morning_3",
  "rafting_pickup_morning_4",
];

const RAFTING_PICKUP_AFTERNOON = [
  "rafting_pickup_afternoon_1",
  "rafting_pickup_afternoon_2",
  "rafting_pickup_afternoon_3",
  "rafting_pickup_afternoon_4",
];

export default function TourDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const { priceMap, money } = usePriceMap();

  const [activeTab, setActiveTab] = useState<"highlights" | "wear" | "map">("highlights");
  const [sliderIndex, setSliderIndex] = useState(0);

  const tour = tours.find((t) => t.slug === slug);

  const sliderImages = tour?.sliderImages ?? [];

  if (!tour) {
    return <NotFound />;
  }

  const relatedTours = tours.filter(
    (t) => t.category === tour.category && t.slug !== tour.slug
  );

  const itineraryDays = tour.hasItinerary && tour.itineraryDays
    ? Array.from({ length: tour.itineraryDays }, (_, i) => i + 1)
    : [];

  const isSailing = tour.isSailing;

  const bookingPath = tour.category === "multi-day"
    ? "/booking-multiday"
    : tour.isRafting
    ? "/booking-rafting"
    : tour.isSailing
    ? "/booking-sailing"
    : "/booking";

  const bookingTo =
    (tour.category === "day-trip" || tour.category === "multi-day") && tour.bookingValue
      ? `${bookingPath}?tour=${encodeURIComponent(tour.bookingValue)}`
      : bookingPath;

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t(tour.category === "day-trip" ? "daytrips_eyebrow" : tour.category === "multi-day" ? "multiday_eyebrow" : "adventure_eyebrow")}
          title={t(tour.titleKey)}
          subtitle={t(tour.subtitleKey)}
          image={tour.image}
          imageAlt={t(tour.titleKey)}
        />

        {/* Image Gallery */}
        {sliderImages.length > 0 && (
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
              <div className="rounded-lg overflow-hidden border border-background-200 aspect-video">
                <img
                  src={sliderImages[sliderIndex].src}
                  alt={sliderImages[sliderIndex].alt}
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                {sliderImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSliderIndex(idx)}
                    className={`w-20 h-14 rounded overflow-hidden border-2 flex-shrink-0 cursor-pointer transition-colors ${
                      sliderIndex === idx
                        ? "border-accent-500"
                        : "border-transparent hover:border-background-300"
                    }`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="mb-8">
              <LocalizedLink
                to={tour.backPath}
                className="inline-flex items-center gap-2 text-sm text-accent-700 hover:text-accent-900 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                {t(tour.backLabelKey)}
              </LocalizedLink>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              {/* Main content */}
              <div className="lg:col-span-2 space-y-10">
                {/* Description / Itinerary */}
                <div>
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                    {tour.hasItinerary
                      ? t("tour_detail_itinerary_label")
                      : t("tour_detail_description_label")}
                  </h2>
                  <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>

                  {tour.hasItinerary && itineraryDays.length > 0 ? (
                    <div className="space-y-6">
                      {itineraryDays.map((day) => (
                        <div
                          key={day}
                          className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6"
                        >
                          <h3 className="font-heading text-lg text-foreground-950 mb-3">
                            {t("tour_detail_day_label")} {day}
                          </h3>
                          <p className="text-sm text-foreground-700 leading-relaxed">
                            {t(`${tour.itineraryPrefixKey}${day}`)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-foreground-700 leading-relaxed">
                      {t(tour.detailDescKey)}
                    </p>
                  )}
                </div>

                {tour.isRafting ? (
                  <>
                    {/* Price Includes */}
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                        {t("rafting_price_includes_title")}
                      </h2>
                      <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                        {RAFTING_PRICE_INCLUDES.map((key) => (
                          <div key={key} className="flex items-start gap-3">
                            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mt-0.5 shrink-0">
                              <i className="ri-check-line text-sm"></i>
                            </span>
                            <span className="text-foreground-700 text-sm">{t(key)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Package Inclusions */}
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                        {t("rafting_package_title")}
                      </h2>
                      <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                      <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
                        <ul className="space-y-3">
                          {RAFTING_PACKAGE.map((key) => (
                            <li key={key} className="flex items-start gap-3">
                              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 mt-0.5 shrink-0">
                                <i className="ri-check-line text-sm"></i>
                              </span>
                              <span className="text-foreground-700 text-sm">{t(key)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Sessions */}
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                        {t("rafting_sessions_title")}
                      </h2>
                      <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-background-100 border border-background-200 rounded-lg p-5 flex items-center gap-3">
                          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                            <i className="ri-sun-line text-lg"></i>
                          </span>
                          <p className="text-sm font-medium text-foreground-950">
                            {t("rafting_session_morning")}
                          </p>
                        </div>
                        <div className="bg-background-100 border border-background-200 rounded-lg p-5 flex items-center gap-3">
                          <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                            <i className="ri-sun-cloudy-line text-lg"></i>
                          </span>
                          <p className="text-sm font-medium text-foreground-950">
                            {t("rafting_session_afternoon")}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Pick-up information */}
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                        {t("rafting_pickup_title")}
                      </h2>
                      <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                          <p className="text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                            <i className="ri-bus-line text-accent-700"></i>
                            {t("rafting_pickup_morning_label")}
                          </p>
                          <ul className="space-y-2">
                            {RAFTING_PICKUP_MORNING.map((key) => (
                              <li key={key} className="flex items-start gap-2 text-sm text-foreground-700">
                                <span className="text-accent-700 mt-0.5"><i className="ri-map-pin-2-line"></i></span>
                                <span>{t(key)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                          <p className="text-sm font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                            <i className="ri-bus-line text-accent-700"></i>
                            {t("rafting_pickup_afternoon_label")}
                          </p>
                          <ul className="space-y-2">
                            {RAFTING_PICKUP_AFTERNOON.map((key) => (
                              <li key={key} className="flex items-start gap-2 text-sm text-foreground-700">
                                <span className="text-accent-700 mt-0.5"><i className="ri-map-pin-2-line"></i></span>
                                <span>{t(key)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="mt-4 bg-accent-100/70 border border-accent-300/60 rounded-lg p-4 flex items-start gap-3">
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                          <i className="ri-information-line text-sm"></i>
                        </span>
                        <p className="text-sm text-foreground-700 leading-relaxed">
                          {t("rafting_pickup_note")}
                        </p>
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                        {t("rafting_notes_title")}
                      </h2>
                      <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 mt-0.5 shrink-0">
                            <i className="ri-error-warning-line text-sm"></i>
                          </span>
                          <span className="text-foreground-700 text-sm">{t("rafting_note_age")}</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 mt-0.5 shrink-0">
                            <i className="ri-shirt-line text-sm"></i>
                          </span>
                          <span className="text-foreground-700 text-sm">{t("rafting_note_clothes")}</span>
                        </li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <>
                    {tour.isDiving && (
                      <div>
                        {/* Highlights */}
                        <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                          {t("tour_detail_highlights_label")}
                        </h2>
                        <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                        <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
                          <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                            {t(tour.highlightsKey)}
                          </p>
                        </div>
                      </div>
                    )}

                    {tour.isDiving && (
                      <div>
                        {/* More info & external link */}
                        <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                          {t("tour_detail_more_info_title")}
                        </h2>
                        <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                        <p className="text-foreground-700 leading-relaxed mb-5">
                          {t("tour_detail_more_info_desc")}
                        </p>
                        <a
                          href={tour.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 ${tour.category === "adventure" ? "bg-secondary-500 hover:bg-secondary-200 text-background-50 hover:text-foreground-950" : "bg-primary-500 hover:bg-primary-600 text-background-50"} px-5 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors`}
                        >
                          <i className="ri-external-link-line"></i>
                          {t("tour_detail_more_info_link")}
                        </a>
                      </div>
                    )}

                    {!tour.isDiving && (
                      <div className="bg-background-100 border border-background-200 rounded-lg">
                      {/* Tab buttons */}
                      <div className="flex overflow-x-auto border-b border-background-200">
                        <button
                          onClick={() => setActiveTab("highlights")}
                          className={`flex-1 py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer transition-colors border-b-2 flex items-center justify-center gap-2 ${
                            activeTab === "highlights"
                              ? "border-accent-500 text-accent-700"
                              : "border-transparent text-foreground-600 hover:text-foreground-900"
                          }`}
                        >
                          <i className="ri-star-line"></i>
                          {t("tour_detail_tab_highlights")}
                        </button>
                        <button
                          onClick={() => setActiveTab("wear")}
                          className={`flex-1 py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer transition-colors border-b-2 flex items-center justify-center gap-2 ${
                            activeTab === "wear"
                              ? "border-accent-500 text-accent-700"
                              : "border-transparent text-foreground-600 hover:text-foreground-900"
                          }`}
                        >
                          <i className="ri-umbrella-line"></i>
                          {t("tour_detail_tab_wear")}
                        </button>
                        {!isSailing && (
                          <button
                            onClick={() => setActiveTab("map")}
                            className={`flex-1 py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium whitespace-nowrap cursor-pointer transition-colors border-b-2 flex items-center justify-center gap-2 ${
                              activeTab === "map"
                                ? "border-accent-500 text-accent-700"
                                : "border-transparent text-foreground-600 hover:text-foreground-900"
                            }`}
                          >
                            <i className="ri-map-pin-line"></i>
                            {t("tour_detail_tab_map")}
                          </button>
                        )}
                      </div>

                      {/* Tab content */}
                      <div className="p-5 md:p-6">
                        {activeTab === "highlights" && (
                          <div>
                            <h3 className="font-heading text-lg text-foreground-950 mb-4">
                              {t("tour_detail_highlights_label")}
                            </h3>
                            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                              {t(tour.highlightsKey)}
                            </p>
                          </div>
                        )}

                        {activeTab === "wear" &&
                          (isSailing ? (
                            <div>
                              <h3 className="font-heading text-base text-foreground-950 mb-2">
                                {t("tour_detail_tab_wear")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("adventure_2_wear")}
                              </p>
                            </div>
                          ) : (
                          <div className="space-y-6">
                            <div>
                              <h3 className="font-heading text-base text-foreground-950 mb-2">
                                {t("tour_wear_clothing_title")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("tour_wear_clothing")}
                              </p>
                            </div>
                            <div>
                              <h3 className="font-heading text-base text-foreground-950 mb-2">
                                {t("tour_wear_bring_title")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("tour_wear_bring")}
                              </p>
                            </div>
                            <div>
                              <h3 className="font-heading text-base text-foreground-950 mb-2">
                                {t("tour_wear_accessibility_title")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("tour_wear_accessibility")}
                              </p>
                            </div>
                            <div>
                              <h3 className="font-heading text-base text-foreground-950 mb-2">
                                {t("tour_wear_car_title")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("tour_wear_car")}
                              </p>
                            </div>
                            <div>
                              <h3 className="font-heading text-base text-foreground-950 mb-2">
                                {t("tour_wear_rainforest_title")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("tour_wear_rainforest")}
                              </p>
                            </div>
                          </div>
                          )
                        )}

                        {activeTab === "map" && (
                          <div>
                            {tour.routeMapImage ? (
                              <div className="rounded-lg overflow-hidden border border-background-200">
                                <img
                                  src={tour.routeMapImage}
                                  alt={`${t(tour.titleKey)} - ${t("tour_detail_tab_map")}`}
                                  className="w-full h-auto object-contain"
                                />
                              </div>
                            ) : (
                              <p className="text-sm text-foreground-600 italic">
                                {t("tour_map_no_map")}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {!tour.isDiving && (
                      <div>
                        <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                          {t("tour_detail_included_label")}
                        </h2>
                        <div className="w-10 h-0.5 bg-accent-500 mb-6"></div>
                        {isSailing ? (
                          <div className="space-y-6">
                            <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
                              <h3 className="font-heading text-base text-foreground-950 mb-3">
                                {t("adventure_2_incl_onboard_title")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("adventure_2_incl_onboard")}
                              </p>
                            </div>
                            <div className="bg-background-100 border border-background-200 rounded-lg p-5 md:p-6">
                              <h3 className="font-heading text-base text-foreground-950 mb-3">
                                {t("adventure_2_incl_lemo_title")}
                              </h3>
                              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                                {t("adventure_2_incl_lemo")}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <ul className="space-y-3">
                            {tour.includedKeys.map((key, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mt-0.5 shrink-0">
                                  <i className="ri-check-line text-sm"></i>
                                </span>
                                <span className="text-foreground-700 text-sm">
                                  {t(key)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-6">
                {!tour.isDiving && (
                <div className="bg-background-100 border border-background-200 rounded-lg p-6">
                  <div className="space-y-4">
                    {!tour.isRafting && (
                      <div className="flex items-center gap-3">
                        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                          <i className="ri-time-line text-lg"></i>
                        </span>
                        <div>
                          <p className="text-xs text-foreground-600 uppercase tracking-wide">
                            {t("tour_detail_duration_label")}
                          </p>
                          <p className="text-sm font-medium text-foreground-950">
                            {t(tour.durationKey)}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="rounded-lg border border-primary-500/30 bg-primary-500/10 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-2 flex items-center gap-1.5">
                        <i className="ri-price-tag-3-line"></i>
                        {t("tour_detail_price_label")}
                      </p>
                      {tour.priceKey ? (
                        <div className="space-y-1">
                          <p className="text-base font-semibold text-foreground-950">
                            {priceMap[tour.priceKey] != null
                              ? money(priceMap[tour.priceKey])
                              : t("tour_detail_price_on_request")}
                            {tour.priceChildKey && priceMap[tour.priceKey] != null && (
                              <span className="text-sm font-medium text-foreground-600 ml-1.5">
                                {isDe ? "pro Erwachsener" : "per adult"}
                              </span>
                            )}
                          </p>
                          {tour.priceChildKey && priceMap[tour.priceChildKey] != null && (
                            <p className="text-sm font-medium text-foreground-900">
                              {money(priceMap[tour.priceChildKey])}{" "}
                              <span className="text-foreground-600">{isDe ? "pro Kind" : "per child"}</span>
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xl font-bold text-foreground-950">
                          {t("tour_detail_price_on_request")}
                        </p>
                      )}
                    </div>

                    {!tour.isRafting && !isSailing && (
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">
                          {t("tour_detail_pickup_time")}
                        </p>
                        <p className="text-sm text-foreground-700">
                          {t("tour_detail_return_time")}
                        </p>
                        <p className="text-xs text-foreground-600 mt-2 leading-relaxed">
                          {t("tour_detail_start_hotel_value")}
                        </p>
                        <p className="text-xs text-foreground-600 leading-relaxed">
                          {t("tour_detail_start_hotel_note")}
                        </p>
                      </div>
                    </div>
                    )}
                  </div>

                  <div className="mt-6 pt-6 border-t border-background-200">
                    <LocalizedLink
                      to={bookingTo}
                      className={`flex items-center justify-center gap-2 ${tour.category === "adventure" ? "bg-secondary-500 hover:bg-secondary-200 text-white hover:text-foreground-950" : "bg-primary-500 hover:bg-primary-600 text-white"} px-5 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors w-full`}
                    >
                      <i className="ri-calendar-check-line"></i>
                      {t("tour_detail_book_cta")}
                    </LocalizedLink>
                    <BookingCompanyNotice isDe={isDe} />
                  </div>
                </div>
                )}

                {/* Related tours */}
                {relatedTours.length > 0 && (
                  <div className="bg-background-100 border border-background-200 rounded-lg p-6">
                    <h3 className="font-heading text-lg text-foreground-950 mb-4">
                      {t("tour_detail_related_label")}
                    </h3>
                    <div className="w-8 h-0.5 bg-accent-500 mb-4"></div>
                    <div className="space-y-3">
                      {relatedTours.map((rt) => (
                        <LocalizedLink
                          key={rt.slug}
                          to={`/tour/${rt.slug}`}
                          className="flex items-center gap-3 group cursor-pointer"
                        >
                          <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 border border-background-200">
                            <img
                              src={rt.image}
                              alt={t(rt.titleKey)}
                              className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-950 group-hover:text-accent-700 transition-colors">
                              {t(rt.titleKey)}
                            </p>
                            <p className="text-xs text-foreground-600">
                              {t(rt.subtitleKey)}
                            </p>
                          </div>
                        </LocalizedLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}